import {
  PublicKey,
  LAMPORTS_PER_SOL
} from "@solana/web3.js";
import * as anchor from "@coral-xyz/anchor";
import { AnchorProvider, BN } from "@coral-xyz/anchor";
import {
  MarinadeUtils,
  Provider
} from "@sunrisestake/marinade-ts-sdk";
import { MAX_NUM_PRECISION } from "./constants";
import {
  getAccount,
  getAssociatedTokenAddressSync
} from "@solana/spl-token";
const ZERO = new BN(0);
const PROGRAM_ID = new PublicKey(
  "sunzv8N3A8dRHwUBvxgRDEbWKk8t7yiHR4FLRgFsTX6"
);
const zip = (a, b, defaultB) => a.map((k, i) => {
  if (b.length <= i)
    return [k, defaultB];
  return [k, b[i]];
});
const toSol = (lamports, precision = MAX_NUM_PRECISION) => lamports.div(new BN(10).pow(new BN(precision))).toNumber() / (LAMPORTS_PER_SOL / 10 ** precision);
var ProgramDerivedAddressSeed = /* @__PURE__ */ ((ProgramDerivedAddressSeed2) => {
  ProgramDerivedAddressSeed2["G_SOL_MINT_AUTHORITY"] = "gsol_mint_authority";
  ProgramDerivedAddressSeed2["M_SOL_ACCOUNT"] = "msol_account";
  ProgramDerivedAddressSeed2["B_SOL_ACCOUNT"] = "bsol_account";
  ProgramDerivedAddressSeed2["EPOCH_REPORT_ACCOUNT"] = "epoch_report";
  ProgramDerivedAddressSeed2["ORDER_UNSTAKE_TICKET_ACCOUNT"] = "order_unstake_ticket_account";
  ProgramDerivedAddressSeed2["LOCK_ACCOUNT"] = "lock_account";
  ProgramDerivedAddressSeed2["LOCK_TOKEN_ACCOUNT"] = "lock_token_account";
  ProgramDerivedAddressSeed2["IMPACT_NFT_MINT_AUTHORITY"] = "impact_nft_mint_authority";
  ProgramDerivedAddressSeed2["IMPACT_NFT_MINT_ACCOUNT"] = "impact_nft_mint_account";
  return ProgramDerivedAddressSeed2;
})(ProgramDerivedAddressSeed || {});
const findProgramDerivedAddress = (config, seed, extraSeeds = []) => {
  const seeds = [
    config.stateAddress.toBuffer(),
    Buffer.from(seed),
    ...extraSeeds
  ];
  return PublicKey.findProgramAddressSync(seeds, config.programId);
};
const findMSolTokenAccountAuthority = (config) => findProgramDerivedAddress(config, "msol_account" /* M_SOL_ACCOUNT */);
const findBSolTokenAccountAuthority = (config) => findProgramDerivedAddress(config, "bsol_account" /* B_SOL_ACCOUNT */);
const findGSolMintAuthority = (config) => findProgramDerivedAddress(
  config,
  "gsol_mint_authority" /* G_SOL_MINT_AUTHORITY */
);
const findEpochReportAccount = (config) => findProgramDerivedAddress(
  config,
  "epoch_report" /* EPOCH_REPORT_ACCOUNT */
);
const findOrderUnstakeTicketAccount = (config, epoch, index) => {
  const epochBuf = Buffer.allocUnsafe(8);
  epochBuf.writeBigInt64BE(epoch, 0);
  const indexBuf = Buffer.allocUnsafe(8);
  indexBuf.writeBigInt64BE(index, 0);
  return findProgramDerivedAddress(
    config,
    "order_unstake_ticket_account" /* ORDER_UNSTAKE_TICKET_ACCOUNT */,
    [epochBuf, indexBuf]
  );
};
const findLockAccount = (config, authority) => findProgramDerivedAddress(config, "lock_account" /* LOCK_ACCOUNT */, [
  authority.toBuffer()
]);
const findLockTokenAccount = (config, authority) => findProgramDerivedAddress(
  config,
  "lock_token_account" /* LOCK_TOKEN_ACCOUNT */,
  [authority.toBuffer()]
);
const findImpactNFTMintAuthority = (config) => findProgramDerivedAddress(
  config,
  "impact_nft_mint_authority" /* IMPACT_NFT_MINT_AUTHORITY */
);
const findImpactNFTMint = (config, authority) => findProgramDerivedAddress(
  config,
  "impact_nft_mint_account" /* IMPACT_NFT_MINT_ACCOUNT */,
  [authority.toBuffer()]
);
const logKeys = (transaction) => {
  transaction.instructions.forEach((instruction, j) => {
    instruction.keys.forEach((key, i) => {
      console.log(j, i, key.pubkey.toBase58());
    });
  });
};
const getTokenAccountNullable = async (connection, tokenAccountAddress) => {
  return getAccount(connection, tokenAccountAddress).catch((error) => {
    if (error.name === "TokenAccountNotFoundError") {
      return null;
    }
    throw error;
  });
};
const getImpactNFT = async (config, authority, provider) => {
  const impactNFTMint = findImpactNFTMint(config, authority)[0];
  const tokenAccountAddress = getAssociatedTokenAddressSync(
    impactNFTMint,
    authority
  );
  const tokenAccount = await getTokenAccountNullable(
    provider.connection,
    tokenAccountAddress
  );
  return {
    mint: impactNFTMint,
    tokenAccount: tokenAccountAddress,
    exists: (tokenAccount?.amount ?? 0) > 0
  };
};
const confirm = (connection) => async (txSig) => connection.confirmTransaction({
  signature: txSig,
  ...await connection.getLatestBlockhash()
});
const setUpAnchor = () => {
  const provider = AnchorProvider.env();
  anchor.setProvider(provider);
  return provider;
};
const ZERO_BALANCE = {
  value: {
    amount: "0",
    decimals: 9,
    uiAmount: 0,
    uiAmountString: "0"
  }
};
const findAllTickets = async (connection, config, epoch, expectedCount) => {
  const tickets = [];
  for (let i = expectedCount - 1; i >= 0; i--) {
    const [orderUnstakeTicketAccount] = findOrderUnstakeTicketAccount(
      config,
      epoch,
      BigInt(i)
    );
    tickets.push(orderUnstakeTicketAccount);
  }
  const accountInfos = await connection.getMultipleAccountsInfo(tickets);
  return accountInfos.map((accountInfo, i) => [
    tickets[i],
    accountInfo
  ]).filter(
    (element) => element[1] !== null
  ).map(([ticket]) => ticket);
};
const proportionalBN = (amount, numerator, denominator) => {
  if (denominator.isZero()) {
    return amount;
  }
  const result = BigInt(amount.toString()) * BigInt(numerator.toString()) / BigInt(denominator.toString());
  return new BN(result.toString());
};
const getStakeAccountInfo = async (stakeAccount, anchorProvider) => {
  const provider = new Provider(
    anchorProvider.connection,
    anchorProvider.wallet,
    {}
  );
  return MarinadeUtils.getParsedStakeAccountInfo(provider, stakeAccount);
};
const getVoterAddress = async (stakeAccount, provider) => {
  const info = await getStakeAccountInfo(stakeAccount, provider);
  if (!info.voterAddress) {
    throw new Error(`Stake account must be delegated`);
  }
  return info.voterAddress;
};
const getValidatorIndex = async (marinadeState, voterAddress) => {
  const { validatorRecords } = await marinadeState.getValidatorRecords();
  const validatorLookupIndex = validatorRecords.findIndex(
    ({ validatorAccount }) => validatorAccount.equals(voterAddress)
  );
  return validatorLookupIndex === -1 ? marinadeState.state.validatorSystem.validatorList.count : validatorLookupIndex;
};
const marinadeTargetReached = (details, percentageStakeToMarinade) => {
  const msolValue = details.mpDetails.msolValue;
  const lpValue = details.lpDetails.lpSolValue;
  const totalMarinade = msolValue.add(lpValue);
  const totalValue = totalMarinade.add(details.bpDetails.bsolValue);
  const limit = proportionalBN(
    totalValue,
    new BN(percentageStakeToMarinade),
    new BN(100)
  );
  const lpShare = details.lpDetails.lpSolValue.muln(1e3).div(totalValue).toNumber() / 10;
  if (lpShare < 9) {
    console.log(
      `LP share is ${lpShare}%, which is below the minimum of 10%. Should send to Marinade.`
    );
    return false;
  }
  return totalMarinade.gt(limit);
};
export {
  PROGRAM_ID,
  ProgramDerivedAddressSeed,
  ZERO,
  ZERO_BALANCE,
  confirm,
  findAllTickets,
  findBSolTokenAccountAuthority,
  findEpochReportAccount,
  findGSolMintAuthority,
  findImpactNFTMint,
  findImpactNFTMintAuthority,
  findLockAccount,
  findLockTokenAccount,
  findMSolTokenAccountAuthority,
  findOrderUnstakeTicketAccount,
  getImpactNFT,
  getStakeAccountInfo,
  getTokenAccountNullable,
  getValidatorIndex,
  getVoterAddress,
  logKeys,
  marinadeTargetReached,
  proportionalBN,
  setUpAnchor,
  toSol,
  zip
};
